import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'shared/components/alerts';
import { partnerUserAlerts } from 'shared/components/alerts/alert-messages';
import { hqRoutes } from 'hq/Routes';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { Header, LwButton, Page, Toolbox, useFormButtons } from 'redesign';
import { invariant } from 'shared/utils/utils';
import { QUERY_KEYS } from 'api/query-keys';
import { useCreatePartnerUser } from './../hooks/use-create-partner';
import { PartnerUserForm } from './partner-user-form';
import { PartnerUserFormData } from './partner-user-form.types';

export const PartnerUserCreate = () => {
  const { id: partnerId } = useParams<'id'>();
  invariant(partnerId);
  const navigate = useNavigate();
  const generatePath = useHqRelativePath();
  const { t } = useTranslation(['hq-screen-specific', 'web-common']);
  const { alertSuccess, alertError } = useAlert();
  const createPartnerUser = useCreatePartnerUser();
  const queryClient = useQueryClient();
  const formMethods = useForm<PartnerUserFormData>();
  const { submitButtonProps, cancelButtonProps } = useFormButtons(createPartnerUser.status);

  const handleSubmit = useCallback(() => {
    formMethods.handleSubmit((data) =>
      createPartnerUser.mutate(
        { ...data, partnerId },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.partners());
            alertSuccess(partnerUserAlerts.success.created);
            navigate(generatePath(hqRoutes.PartnerUsers, { id: partnerId }));
          },
          onError: (e) => alertError(e),
        }
      )
    )();
  }, [
    formMethods,
    createPartnerUser,
    partnerId,
    queryClient,
    alertSuccess,
    navigate,
    generatePath,
    alertError,
  ]);

  return (
    <FormProvider {...formMethods}>
      <Page
        header={
          <Header titleText={t('partners.users.create.title', { ns: 'hq-screen-specific' })} />
        }
        toolbox={
          <Toolbox underline>
            <LwButton onClick={handleSubmit} iconColorMode="stroke" {...submitButtonProps}>
              {t('buttons.create', { ns: 'web-common' })}
            </LwButton>
            <LwButton
              onClick={() => navigate(generatePath(hqRoutes.PartnerUsers, { id: partnerId }))}
              iconColorMode="fill"
              {...cancelButtonProps}
            >
              {t('buttons.cancel', { ns: 'web-common' })}
            </LwButton>
          </Toolbox>
        }
      >
        <PartnerUserForm onSubmit={handleSubmit} state="create" />
      </Page>
    </FormProvider>
  );
};
